/**
 * Content-editor and live co-editing rules shared by every consuming app.
 * Browser-safe and framework-neutral: no DOM, editor or application imports.
 * Visual treatment (presence tones, cursor rendering) stays with the host app's
 * design tokens; the package only decides identity, wording and safety.
 */

const PRESENCE_TEXT_LIMIT = 120;

/** A remote collaborator as reported by the server. Names come from the server, never the client. */
export type PresencePeer = { sessionId: string; actorId: string; name?: string | null; canEdit?: boolean };

/**
 * Stable index into a host-supplied list of presence styles, like Solution Studio's
 * presenceColorForId. The same person keeps the same style across sessions.
 */
export function presenceStyleIndex(id: string, styleCount: number): number {
  if (!Number.isInteger(styleCount) || styleCount < 1) throw new RangeError('styleCount must be a positive whole number.');
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  return hash % styleCount;
}

/** Display name for a peer. Unknown people show Solution Studio's fallback, "Collaborator". */
export function presenceName(peer: Pick<PresencePeer, 'name'>): string {
  const name = typeof peer.name === 'string' ? peer.name.trim() : '';
  return name ? name.slice(0, PRESENCE_TEXT_LIMIT) : 'Collaborator';
}

export function presenceInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Accessible summary used by the presence avatars. */
export function presenceSummary(peers: readonly PresencePeer[]): string {
  const editors = peers.filter(peer => peer.canEdit !== false).length;
  const viewers = peers.length - editors;
  const parts = [`${editors} other ${editors === 1 ? 'editor' : 'editors'} present`];
  if (viewers) parts.push(`${viewers} viewing`);
  return parts.join(', ');
}

/** Solution Studio's capped exponential reconnect delay after a dropped live connection. */
export function reconnectDelay(attempt: number): number {
  const step = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 0;
  return Math.min(30_000, 1000 * 2 ** Math.min(step, 5));
}

/**
 * Accepts only complete http(s) addresses without embedded credentials, or mailto links.
 * Returns the trimmed address, or undefined when it must not be stored or opened.
 */
export function safeEditorLink(value: string): string | undefined {
  const href = value.trim();
  if (!href || /[\u0000-\u0020\u007f]/.test(href)) return undefined;
  try {
    const url = new URL(href);
    if (url.protocol === 'mailto:' && url.pathname) return href;
    if ((url.protocol === 'http:' || url.protocol === 'https:') && url.hostname && !url.username && !url.password) return href;
  } catch {
    return undefined;
  }
  return undefined;
}

export const EDITOR_IMAGE_DATA_URL_LIMIT = 2_800_000;

export interface EditorImagePolicyOptions {
  /** Same-origin image addresses the host app serves, for example its file-content route. Anchor each pattern. */
  allowedPaths: readonly RegExp[];
  /** Largest inline data URL accepted, in characters. Defaults to EDITOR_IMAGE_DATA_URL_LIMIT. */
  maxDataUrlLength?: number;
}

const INLINE_IMAGE = /^data:image\/(?:png|jpeg|gif|webp);base64,[a-z0-9+/]+={0,2}$/i;

/**
 * Builds the editor's image source check: host-approved paths and bounded inline
 * PNG/JPEG/GIF/WebP data only. Remote and script-capable sources are always refused.
 */
export function createEditorImagePolicy(options: EditorImagePolicyOptions): (value: string) => string | undefined {
  const paths = [...options.allowedPaths];
  for (const pattern of paths) {
    if (!pattern.source.startsWith('^') || !pattern.source.endsWith('$')) throw new Error('Editor image paths must be anchored with ^ and $.');
  }
  const limit = options.maxDataUrlLength ?? EDITOR_IMAGE_DATA_URL_LIMIT;
  return (value: string) => {
    const src = value.trim();
    if (paths.some(pattern => { pattern.lastIndex = 0; return pattern.test(src); })) return src;
    if (src.length <= limit && INLINE_IMAGE.test(src)) return src;
    return undefined;
  };
}
